import { inject, Injectable } from '@angular/core';
import { invalidateSignals, SessionStorage, SessionStorageService, SignalStateProperty, SignalStateService } from '@mwe/utils';
import { NavbarConfig, NavigatingBackTo } from '@mwe/models';
import { Router } from '@angular/router';

const initialUrlKey = 'initialUrl';
const wizardCancelUrlKey = 'wizardCancelUrl';
const wizardSuccessUrlKey = 'wizardSuccessUrl';
const navigatingBackToKey = 'navigatingBackTo';

@Injectable({ providedIn: 'root' })
@SignalStateService({ decorateAll: false })
export class NavigationStateService {
  private readonly storage = inject(SessionStorageService);
  private readonly router = inject(Router);

  @SessionStorage(initialUrlKey)
  private _initialUrlKey: string;

  @SessionStorage(wizardCancelUrlKey)
  private _wizardCancelUrl: string;

  @SessionStorage(wizardSuccessUrlKey)
  private _wizardSuccessUrl: string;

  @SessionStorage(navigatingBackToKey)
  private _navigatingBackTo: NavigatingBackTo[];

  // no storage
  private _navbarConfig: NavbarConfig;

  clearNavigationUrls(): void {
    this.clearWizardUrls();
    this.storage.clear(navigatingBackToKey);
    invalidateSignals(this);
  }

  clearWizardUrls(): void {
    this.storage.clear(wizardCancelUrlKey);
    this.storage.clear(wizardSuccessUrlKey);
    invalidateSignals(this);
  }

  getAndClearInitialUrl(): string {
    const url = this._initialUrlKey;
    this.storage.clear(initialUrlKey);
    return url;
  }

  navigateInitialUrl(): void {
    const initialUrl = this.getAndClearInitialUrl();
    this.router.navigateByUrl(initialUrl ?? '/');
  }

  setInitialUrl(url: string): void {
    this._initialUrlKey = url;
  }

  @SignalStateProperty()
  get wizardCancelUrl(): string {
    return this._wizardCancelUrl ?? `/`;
  }

  set wizardCancelUrl(value: string) {
    this._wizardCancelUrl = value;
  }

  @SignalStateProperty()
  get wizardSuccessUrl(): string {
    return this._wizardSuccessUrl ?? `/`;
  }

  set wizardSuccessUrl(value: string) {
    this._wizardSuccessUrl = value;
  }

  get navbarConfig(): NavbarConfig {
    return this._navbarConfig;
  }

  set navbarConfig(value: NavbarConfig) {
    this._navbarConfig = value;
  }

  getNavigatingBackTo(): NavigatingBackTo[] {
    return this._navigatingBackTo ?? [];
  }

  getLatestNavigatingBackTo(): NavigatingBackTo {
    const list = this.getNavigatingBackTo();

    return list[list.length - 1];
  }

  addNavigatingBackTo(link: NavigatingBackTo): void {
    if (!link) {
      return;
    }

    this._navigatingBackTo = [...this.getNavigatingBackTo(), link];
  }

  removeLatestNavigatingBackTo(): void {
    this.getNavigatingBackTo().splice(-1);
    this._navigatingBackTo = this.getNavigatingBackTo();
  }
}
